import {
  Stack,
  CircularProgress,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TablePagination,
  Chip,
  TableBody,
  TableCell,
  TableRow,
  Tooltip} from "@mui/material"
import React from "react";
import { ApolloQueryResult } from "@apollo/client/core/types";
import { useTranslator } from "hooks/translator";
import { DraggableProvided, Draggable } from "@hello-pangea/dnd";
import { KeyboardDoubleArrowUp } from "@mui/icons-material";
import { t } from "utils/i18n";
import { Option } from '../../../../types/option';

export function reorder<T>(list: T[], startIndex: number, endIndex: number) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
}

export function cloneRow(option: Option, index: number, provided: DraggableProvided) {
  return <Table ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                size='small'
                sx={{m: 1, border: 1, borderColor: '#f5f5f5', ...provided.draggableProps.style}}>
      <TableBody>
        <TableRow sx={{backgroundColor: 'white'}}>
          <TableCell>
            <Chip size='small'
                  color='primary'
                  variant='outlined'
                  label={<Typography variant='overline' sx={{textAlign: 'center', display: 'block'}}>{index + 1}</Typography>}/>
          </TableCell>
          {Object.entries(option.tuple!).map(val => <TableCell key={[val[0], option.value, val[1]].join('_')}>{val[1]}</TableCell>)}
        </TableRow>
      </TableBody>
    </Table>
}

export function rowIcon(value: string, index: number, isSelected: boolean) {
  return <TableCell key={value + "_select"}>
            <Tooltip placement='left' title={isSelected ? t('select.reorder') : t('select.selectitem')}>
              {isSelected
                ? <Chip size='small'
                        variant='outlined'
                        label={<Typography variant='overline' sx={{textAlign: 'center', display: 'block'}}>{index + 1}</Typography>}/>
                : <KeyboardDoubleArrowUp sx={{verticalAlign: 'middle', padding: '0px'}} fontSize='small' color='disabled'/> }
              </Tooltip>
          </TableCell>
}

export function DraggableDivider(index: number, columns: number) {
  return <Draggable key="divider-drag" draggableId='divider' index={index} isDragDisabled={true}>
    {(provided, _snapshot) =>
      <TableRow key="divider"
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                sx={{ backgroundColor: '#f5f5f5', ...provided.draggableProps.style }}>
        <TableCell colSpan={columns + 1}/>
      </TableRow>}
  </Draggable>
} 

export interface CancellableRequest extends Promise<ApolloQueryResult<any>> {
  cancel: () => void
}

type ResultTableProps = {
  loading: boolean
  empty: boolean
  header: JSX.Element
  body: JSX.Element
}

type PagedResultTableProps = ResultTableProps & {
  page: number,
  total: number,
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void
}

export function focusRef(ref: React.RefObject<HTMLDivElement>) {
  window.setTimeout(() => ref.current?.focus(), 0);
}

export const Results = ({loading, empty, header, body}: ResultTableProps) => {
  const { t } = useTranslator()

  if (loading) return <Stack direction='row' spacing='15px' sx={{padding: '8px'}}>
      <CircularProgress color="inherit" size={20} />
      <Typography>{t("select.loading")}</Typography>
    </Stack>
  if (empty) return <Typography sx={{padding: '8px'}}>{t("select.nooptions")}</Typography>
  return <TableContainer className='large-table-container'
      sx={{
        height: "fit-content",
        maxHeight: "450px",
        maxWidth: '100%',
      }}>
    <Table stickyHeader size='small'>
      <TableHead>
        {header}
      </TableHead>
      {body}
    </Table>
  </TableContainer>
}

export const PagedResults = ({loading, empty, page, total, onPageChange, header, body}: PagedResultTableProps) => {
  const { t } = useTranslator()

  if (loading) return <Stack direction='row' spacing='15px' sx={{padding: '8px'}}>
      <CircularProgress color="inherit" size={20} />
      <Typography>{t("select.loading")}</Typography>
    </Stack>
  if (empty) return <Typography sx={{padding: '8px'}}>{t("select.nooptions")}</Typography>
  return <>
    <TableContainer className='large-table-container'
        sx={{
          height: "fit-content",
          maxHeight: "450px",
          maxWidth: '100%',
        }}>
      <Table stickyHeader size='small'>
        <TableHead>
          {header}
        </TableHead>
        {body}
      </Table>
    </TableContainer>
    <TablePagination
          component="div"
          count={total}
          rowsPerPage={100}
          rowsPerPageOptions={[]}
          page={page}
          onPageChange={onPageChange}
        />
  </>
}
